import type { KeyboardEvent } from 'react';

import { cn } from '@shared/lib/cn';
import { formatLocalDate } from '@shared/lib/date';
import { Icon } from '@shared/ui/icon';
import { TABLE_HOVER_ATTR, TABLE_HOVER_CELL_CLASS } from '@shared/ui/table';

import { formatFundPercent, formatMillionWon, formatMultiple } from '../lib/fund-format';
import type { Fund } from '../model/fund';
import {
  FUND_ROW_CELL_CLASS,
  FUND_ROW_CLASS,
  FUND_ROW_WIDTHS,
  fundRowCellWidth,
} from './fund-row.constant';
import { FUND_ROW_HEADERS } from './fund-row.text';

export type FundRowProps =
  | { kind: 'header'; widths?: readonly number[] }
  | { kind: 'row'; item: Fund; widths?: readonly number[]; onClick?: (fund: Fund) => void };

function fundRowValues(fund: Fund): string[] {
  return [
    fund.name,
    fund.commentInvestmentTarget,
    fund.fundDuration[0] ? formatLocalDate(fund.fundDuration[0]) : '',
    formatMillionWon(fund.volumeCashCapitalContribution),
    `${fund.fundingRound[0]} ~ ${fund.fundingRound[1]}`,
    formatFundPercent(fund.irr1),
    formatMultiple(fund.tvpi),
    formatFundPercent(fund.burnrate),
    String(fund.NumberOfExitComplete + fund.NumberOfExitPartial),
  ];
}

/**
 * 펀드 목록 표의 한 줄(9칸). `<tr>` 이라 `<table>` 안에 넣는다(FundListSection).
 * 칸 너비는 비율(`widths`, 기본 Figma 1380 의 칸 너비, DD-19)이라 표 폭을 채운다(DD-15 H2).
 * 머리 칸 오른쪽의 화살표는 동작이 없는 자리다(기능 추가 예정).
 */
export function FundRow(props: FundRowProps) {
  const widths = props.widths ?? FUND_ROW_WIDTHS;

  if (props.kind === 'header') {
    return (
      <tr data-kind="header" className={FUND_ROW_CLASS.header}>
        {FUND_ROW_HEADERS.map((label, index) => (
          <th
            key={label}
            scope="col"
            style={{ width: fundRowCellWidth(widths, index) }}
            className={FUND_ROW_CELL_CLASS.header}
          >
            <span className="flex items-center justify-between gap-1">
              <span>{label}</span>
              <Icon name="chevron-down" size="xs" className="text-fg-muted" />
            </span>
          </th>
        ))}
      </tr>
    );
  }

  const { item, onClick } = props;
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(item);
    }
  };

  return (
    <tr
      data-kind="row"
      {...TABLE_HOVER_ATTR.row}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? () => onClick(item) : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      className={FUND_ROW_CLASS.row}
    >
      {fundRowValues(item).map((value, index) => (
        <td
          key={FUND_ROW_HEADERS[index]}
          style={{ width: fundRowCellWidth(widths, index) }}
          className={cn(FUND_ROW_CELL_CLASS.row, TABLE_HOVER_CELL_CLASS.row)}
        >
          {value}
        </td>
      ))}
    </tr>
  );
}
